"use strict";
/**
 * This module provides access to XMLHttpRequest based service calling utilities
 *
 * @preferred
 */
Object.defineProperty(exports, "__esModule", { value: true });
var _ = require("lodash");
/**
 * The default XMLHttpRequest transport provider
 * @type {any}
 */
var transportProvider;
/**
 * For node.js or alternative platforms you can use setTransportProvider to point to an XHR alternative
 * @param  {any}    alternativeXMLHttpRequest   Platform specific or alternative XMLHttpRequest implementation
 * @return {void}                               This method does not return anything
 */
function setTransportProvider(alternativeXMLHttpRequest) {
    // console.log( "[XHR] Alternative provider set" );
    transportProvider = alternativeXMLHttpRequest;
}
exports.setTransportProvider = setTransportProvider;
/**
 * Create a new instance of an XMLHttpRequest object
 * @return {any} The XMLHttpRequest instance
 */
function createTransport() {
    if (transportProvider) {
        // console.log( "[XHR] Creating user provided transport" );
        return new transportProvider();
    }
    else if (typeof XMLHttpRequest !== "undefined") {
        // Need to set withCredentials for support for set-cookie header
        //
        // console.log( "[XHR] Creating default transport" );
        var xhr = new XMLHttpRequest();
        xhr.withCredentials = true;
        return xhr;
    }
    else {
        throw "No XMLHttpRequest provider available";
    }
}
exports.createTransport = createTransport;
/**
 * Possible XMLHttpRequest call methods
 * @type {enum}
 */
var EXMLHttpRequestMethod;
(function (EXMLHttpRequestMethod) {
    EXMLHttpRequestMethod[EXMLHttpRequestMethod["GET"] = 0] = "GET";
    EXMLHttpRequestMethod[EXMLHttpRequestMethod["POST"] = 1] = "POST";
    EXMLHttpRequestMethod[EXMLHttpRequestMethod["PUT"] = 2] = "PUT";
    EXMLHttpRequestMethod[EXMLHttpRequestMethod["DELETE"] = 3] = "DELETE";
    EXMLHttpRequestMethod[EXMLHttpRequestMethod["PATCH"] = 4] = "PATCH";
})(EXMLHttpRequestMethod = exports.EXMLHttpRequestMethod || (exports.EXMLHttpRequestMethod = {}));
/**
 * Provides a wrapper for an XMLHttpRequest call
 * @type {class}
 */
var XHR = /** @class */ (function () {
    function XHR() {
        /**
         * The timeout value for an XMLHttpRequest call
         * @type {number}
         */
        this.timeout = 30000;
    }
    XHR.prototype.appendURL = function (url, parameters) {
        var parameterString = "";
        switch (typeof parameters) {
            case "string":
            case "number":
                {
                    parameterString = parameters;
                }
                break;
            case "boolean":
                {
                    parameterString = parameters ? "true" : "false";
                }
                break;
            case "object":
                {
                    var parameterList = [];
                    if (_.isArray(parameters)) {
                        parameterList = parameters;
                    }
                    else {
                        _.each(parameters, function (value, key) {
                            if (value !== undefined) {
                                parameterList.push(key + "=" + value);
                            }
                        });
                    }
                    parameterString = parameterList.join("&");
                }
                break;
            default:
                {
                    // NOOP
                }
        }
        // Don't append anything when an empty parameterString resulted.
        // Otherwise, if a question mark (?) is already present in the URL then append as an extra
        // parameter with an ampersand (&) instead.
        //
        if (parameterString !== "") {
            url += (/\?/.test(url) ? "&" : "?") + parameterString;
        }
        return url;
    };
    /**
     * Sets the XMLHttpRequest timeout to another value then the default 30 seconds
     * @param  {number} timeout The new timeout value in milliseconds
     * @return {number}         A timeout in milliseconds
     */
    XHR.prototype.setTimeout = function (timeout) {
        return this.timeout = timeout;
    };
    /**
     * Return the current XMLHttpRequest timeout value
     * @return {number} A timeout in milliseconds
     */
    XHR.prototype.getTimeout = function () {
        return this.timeout;
    };
    /**
     * Performs an XMLHttpRequest call
     * @param  {IXMLHttpRequestParameters}  params  The call parameters
     * @return {Promise<IXMLHttpRequestResponse>} A promise that will resolve or reject depending on the call response
     */
    XHR.prototype.call = function (params) {
        var _this = this;
        return new Promise(function (resolve, reject) {
            // Create the XMLHttpRequest instance
            //
            _this.xhr = createTransport();
            // Start the fallback timeout timer with a half second extra buffer
            //
            _this.timer = setTimeout(function () {
                // Create an error response
                //
                reject({
                    status: 408,
                    response: "Request timed out"
                });
            }, _this.timeout + 500);
            // Setup the onreadystatechange listener which listens to the actual response
            //
            _this.xhr.onreadystatechange = function () {
                // console.log( "[XHR] readystate change", this.xhr.readyState );
                if (_this.xhr.readyState === 4) {
                    // Stop our fallback timer
                    //
                    clearTimeout(_this.timer);
                    // You are not allowed to access the status before readystate 4
                    //
                    var responseStatus = parseInt(_this.xhr.status, 10);
                    var responseHeaders = _this.xhr.getAllResponseHeaders();
                    // Check if the response status is a success
                    //
                    if ((responseStatus >= 200 && responseStatus < 300) || responseStatus === 1223) {
                        // Internet Explorer mangles the 204 no content status code hence the 1223 check
                        //
                        if (responseStatus === 1223) {
                            responseStatus = 204;
                        }
                        // Create the success response
                        //
                        var response = _this.xhr.responseText;
                        if (params.contentType === "application/json") {
                            // Try to JSON parse the response before returning it
                            //
                            try {
                                resolve({
                                    status: responseStatus,
                                    response: JSON.parse(response),
                                    xhr: _this.xhr
                                });
                            }
                            catch (jsonParseException) {
                                if (console && typeof console.warn === "function") {
                                    console.warn("[XHR] Error parsing JSON response", jsonParseException);
                                }
                                resolve({
                                    status: responseStatus,
                                    response: response,
                                    xhr: _this.xhr
                                });
                            }
                        }
                        else {
                            resolve({
                                status: responseStatus,
                                response: response,
                                xhr: _this.xhr
                            });
                        }
                    }
                    else {
                        // Create an error response
                        //
                        var errorResponse = _this.xhr.responseText;
                        var statusText = _this.xhr.statusText;
                        if (params.contentType === "application/json") {
                            // Try to JSON parse the error response before returning it
                            //
                            try {
                                reject({
                                    status: responseStatus,
                                    statusText: statusText,
                                    response: JSON.parse(errorResponse),
                                    responseHeaders: responseHeaders,
                                    xhr: _this.xhr
                                });
                            }
                            catch (jsonParseException) {
                                // console.warn( "[XHR] Error parsing JSON error response", jsonParseException );
                                reject({
                                    status: responseStatus,
                                    statusText: statusText,
                                    response: errorResponse,
                                    responseHeaders: responseHeaders,
                                    xhr: _this.xhr,
                                    exception: jsonParseException
                                });
                            }
                        }
                        else {
                            reject({
                                status: responseStatus,
                                statusText: statusText,
                                response: errorResponse,
                                responseHeaders: responseHeaders,
                                xhr: _this.xhr
                            });
                        }
                    }
                }
            };
            // Add the request data to the URL if needed
            // Remember that we need to do this before the @open call
            //
            if (params.data && params.method === EXMLHttpRequestMethod.GET) {
                params.url = _this.appendURL(params.url, params.data);
                params.data = null;
            }
            // Open the XMLHttpRequest
            //
            // console.log( "[XHR] Opening call", EXMLHttpRequestMethod[ params.method ], params.url, true, params.username, params.password );
            _this.xhr.open(EXMLHttpRequestMethod[params.method], params.url, true, params.username, params.password);
            // Set the timeout value
            // Do this afer .open because IE will throw a hissy fit if you don't
            //
            _this.xhr.timeout = _this.timeout;
            // Prepare the call data
            //
            var callData;
            var dataType = typeof params.data;
            if (dataType === "string") {
                // Change new lines in the request body to match protocol specs
                //
                callData = params.data.replace(/\r?\n/g, "\r\n");
            }
            else if (!!params.data && (dataType === "object" || dataType === "function")) {
                // Stringify objects before transport
                //
                callData = JSON.stringify(params.data).replace(/\r?\n/g, "\r\n");
            }
            // Set all the request headers
            //
            dataType = typeof params.headers;
            if (!!params.headers && (dataType === "object" || dataType === "function")) {
                for (var name in params.headers) {
                    if (params.headers.hasOwnProperty(name) && typeof params.headers[name] === "string") {
                        _this.xhr.setRequestHeader(name, params.headers[name]);
                    }
                }
            }
            // Check for missing ContentType header
            //
            if (!params.headers["Content-Type"] && params.contentType) {
                _this.xhr.setRequestHeader("Content-Type", params.contentType);
            }
            // Let's try to perform the call and catch any exceptions to ensure
            // we can always at least reject the promise
            //
            try {
                // console.log( "[XHR] Sending data", callData );
                _this.xhr.send(callData);
            }
            catch (xhrError) {
                if (console && typeof console.error === "function") {
                    console.error("[XHR] Error during request", xhrError);
                }
                reject({
                    status: 500,
                    response: xhrError
                });
            }
        });
    };
    return XHR;
}());
exports.XHR = XHR;
